import {FormProvider, useController, useForm} from 'react-hook-form';
import {Text} from '@treatwell/ui';
import {
  Button,
  FormInput,
  Message,
  PasswordInput,
} from '@treatwell/ui/foundations';
import {routes} from '../../../../../config/routes';
import {generateLoginUrl} from '../../../../../shared/utils/login-url';
import {
  TURNSTILE_FORM_FIELD,
  TurnstileWidget,
} from '../../../../turnstile/TurnstileWidget';
import {getLanguagePath} from '../../../../utils/language';
import {useSubmitHandler} from './useSubmitHandler';
import styles from './Form.module.css';

export type FormInputs = {
  email: string;
  password: string;
  [TURNSTILE_FORM_FIELD]: string;
};

export const Form = () => {
  const methods = useForm<FormInputs>();
  const {control, handleSubmit, setError, clearErrors, setValue, formState} =
    methods;
  const onSubmit = useSubmitHandler({
    handleSubmit,
    setError,
    clearErrors,
    setValue,
  });
  const serverErrorMessage = formState.errors.root?.serverError?.message;
  const {
    field: email,
    fieldState: {error: emailError},
  } = useController({
    control,
    name: 'email',
    rules: {
      required: 'Enter your email address',
      pattern: {
        value: /\S+@\S+\.\S+/,
        message: 'Enter a valid email address',
      },
    },
  });
  const {
    field: password,
    fieldState: {error: passwordError},
  } = useController({
    control,
    name: 'password',
    rules: {required: 'Enter your password'},
  });

  const {hostname, pathname, search} = window.location;
  const languagePath = getLanguagePath(pathname);
  const route = new URLSearchParams(search).get('route') ?? '/';

  return (
    <FormProvider {...methods}>
      <form data-cy="LoginForm" className={styles.grid} onSubmit={onSubmit}>
        {serverErrorMessage ? (
          <Message type="error" data-cy="LoginServerError">
            <Text type="caption">{serverErrorMessage}</Text>
          </Message>
        ) : null}
        <FormInput
          label="Email"
          type="email"
          autoComplete="email"
          isInvalid={Boolean(emailError)}
          errorMessage={emailError?.message}
          {...email}
        />
        <PasswordInput
          label="Password"
          autoComplete="current-password"
          showPasswordLabel="Show password"
          hidePasswordLabel="Hide password"
          isInvalid={Boolean(passwordError)}
          errorMessage={passwordError?.message}
          {...password}
        />
        <TurnstileWidget />
        <Button
          type="submit"
          disabled={formState.isSubmitting}
          isLoading={formState.isSubmitting}
          data-cy="LoginSubmitButton"
        >
          Log in
        </Button>
        <a
          href={`${languagePath}${routes.REGISTER_PATH}${search}`}
          className={styles.link}
          data-cy="LoginCreateAccountLink"
        >
          <Text type="captionHeavy">Create new account</Text>
        </a>
        <a
          href={`${languagePath}${generateLoginUrl(hostname, encodeURIComponent(route))}#password-page`}
          className={styles.link}
          data-cy="LoginForgotPasswordLink"
        >
          <Text type="captionHeavy">Forgot your password?</Text>
        </a>
      </form>
    </FormProvider>
  );
};
